import { useState, type MouseEvent } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const DOCUMENT_NAME_REQUIRED_MESSAGE = 'Document name is required';

/** Keeps clicks inside the chip from focusing the combobox search or opening its list. */
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

/**
 * ZOHO-1467: Shows a selected document's name for one work order and lets the user rename it.
 * The master document name is never changed; saving the master name (or no change) clears the edit.
 * ZOHO-1468: Rendered inline inside the document's chip in the Description of Documents combobox.
 * Million's auto-compiler drops the input's onChange in the editing branch, so it skips this component.
 */
// million-ignore
export const WorkOrderDocumentNameField = ({
  masterName,
  editedName,
  onSave,
}: {
  masterName: string;
  editedName?: string | null;
  onSave: (editedName: string | undefined) => void;
}) => {
  const currentName = editedName ?? masterName;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    setDraft(currentName);
    setError(null);
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(currentName);
    setError(null);
    setIsEditing(false);
  };

  const save = () => {
    const name = draft.trim();
    if (!name) {
      setError(DOCUMENT_NAME_REQUIRED_MESSAGE);
      return;
    }
    onSave(name === masterName ? undefined : name);
    setError(null);
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <span
        className="inline-flex items-center gap-1"
        title={editedName ? `Default name: ${masterName}` : undefined}
      >
        {currentName}
        <button
          type="button"
          aria-label={`Edit document name for ${masterName}`}
          className="rounded-full text-primary hover:text-focuscolor focus:outline-none focus-visible:ring-1 focus-visible:ring-focuscolor"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            startEditing();
          }}
        >
          <Pencil className="h-3 w-3" />
        </button>
      </span>
    );
  }

  return (
    // data-editing hides the chip's remove button, so ✕ here only means Cancel.
    <span data-editing className="inline-flex flex-col gap-0.5" onClick={stopPropagation}>
      {/* One chip line high, so the chip keeps its height and the error goes on the next line. */}
      <span className="inline-flex h-5 items-center gap-1">
        <input
          aria-label={`Document name for ${masterName}`}
          aria-invalid={!!error}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            // Keep keys away from the combobox (Enter, Backspace and Escape act on it).
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          className={cn(
            'h-5 w-44 rounded border border-bordercolor bg-white px-2 py-0 text-sm leading-none font-normal text-primary outline-none focus:border-focuscolor',
            error && 'border-error focus:border-error'
          )}
          autoFocus
        />
        <button
          type="button"
          aria-label="Save"
          className="rounded-full text-primary hover:text-focuscolor focus:outline-none focus-visible:ring-1 focus-visible:ring-focuscolor"
          onClick={save}
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label="Cancel"
          className="rounded-full text-primary hover:text-error focus:outline-none focus-visible:ring-1 focus-visible:ring-focuscolor"
          onClick={cancel}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
      {error && <span className="text-error text-xs font-normal">{error}</span>}
    </span>
  );
};
